import { toDateInputValue } from "../../../utils/appointmentLabels";
import { INDIVIDUAL_SESSION_COLOR, eventKindLabel } from "../../../constants/calendarSessionTypes";

/** Часы сетки календаря: 00:00–23:00 */
export const CALENDAR_HOURS = Array.from({ length: 24 }, (_, i) => i);

export const GROUP_COLORS = [
  "#2563eb",
  "#16a34a",
  "#ca8a04",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#ea580c",
  "#db2777",
];

export function weekStart(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return toDateInputValue(d);
}

export function addDays(dateStr, n) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + n);
  return toDateInputValue(d);
}

export function monthGridDays(anchor) {
  const d = new Date(`${anchor.slice(0, 7)}-01T12:00:00`);
  const start = weekStart(toDateInputValue(d));
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export function sessionColor(sess, groupIndex = 0) {
  if (!sess) {
    return GROUP_COLORS[groupIndex % GROUP_COLORS.length];
  }
  if (sess.session_kind === "event") {
    if (sess.event_kind === "competition") return "#b45309";
    return "#64748b";
  }
  if (sess.training_format === "individual" || sess.individualClient) {
    return INDIVIDUAL_SESSION_COLOR;
  }
  return (
    sess.group?.calendar_color ||
    GROUP_COLORS[groupIndex % GROUP_COLORS.length]
  );
}

export function sessionLabel(sess) {
  if (!sess) return "";
  if (sess.session_kind === "event") {
    return sess.title || eventKindLabel(sess.event_kind);
  }
  if (sess.training_format === "individual" || sess.individualClient) {
    const name =
      sess.individualClient?.full_name ||
      sess.individual_client?.full_name ||
      "Клиент";
    return `Индивидуально · ${name}`;
  }
  const g = sess.group?.name || "Группа";
  const dir = sess.group?.sport_direction;
  return dir ? `${g} · ${dir}` : g;
}

export function isIndividualSession(sess) {
  return (
    sess?.session_kind === "training" &&
    (sess.training_format === "individual" || Boolean(sess.patient_id))
  );
}

export function isEventSession(sess) {
  return sess?.session_kind === "event";
}

export function isToday(dateStr) {
  return dateStr === toDateInputValue();
}

export function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

export function timeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = String(timeStr).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function addMinutesToTime(timeStr, minutes) {
  const total = timeToMinutes(timeStr) + minutes;
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function splitDateTimeLocal(value) {
  if (!value) return { date: "", time: "" };
  const normalized =
    value.includes("T") && value.length <= 16 ? value : toLocalInput(value);
  const [date, time = ""] = normalized.split("T");
  return { date, time: time.slice(0, 5) };
}

export function sessionScheduleFromIso(startIso, endIso) {
  const start = splitDateTimeLocal(toLocalInput(startIso));
  const end = splitDateTimeLocal(toLocalInput(endIso));
  return {
    date: start.date,
    startTime: start.time || "09:00",
    endTime: end.time || addMinutesToTime(start.time || "09:00", 60),
  };
}

export function sessionScheduleToIso({ date, startTime, endTime }) {
  if (!date || !startTime || !endTime) return { start: null, end: null };
  return {
    start: new Date(`${date}T${startTime}:00`).toISOString(),
    end: new Date(`${date}T${endTime}:00`).toISOString(),
  };
}

/** Высота одного часа в недельной сетке (px), должна совпадать с CSS --cal-week-hour-px. */
export const CALENDAR_HOUR_ROW_PX = 60;

export function sessionStartHour(sess) {
  const start = new Date(sess.start_datetime);
  return start.getHours() + start.getMinutes() / 60;
}

export function sessionEndHour(sess) {
  const end = new Date(sess.end_datetime);
  return end.getHours() + end.getMinutes() / 60;
}

/** Занятия, которые начинаются в этом часовом слоте (не дублировать на каждый пересекающийся час). */
export function sessionsStartingInHour(sessions, hour) {
  return sessions.filter((sess) => {
    const startH = sessionStartHour(sess);
    return startH >= hour && startH < hour + 1;
  });
}

/** Сколько часовых строк занимает занятие (дневной вид). */
export function sessionDurationRows(sess) {
  const start = new Date(sess.start_datetime);
  const end = new Date(sess.end_datetime);
  const mins = Math.max(30, (end - start) / 60000);
  return Math.max(1, Math.ceil(mins / 60));
}

/** Позиция и высота карточки в ячейке недельной сетки. */
export function weekGridSessionStyle(sess, hour) {
  const startH = sessionStartHour(sess);
  const endH = sessionEndHour(sess);
  const durationH = Math.max(0.25, endH - startH);
  const offsetPx = (startH - hour) * CALENDAR_HOUR_ROW_PX;
  const heightPx = durationH * CALENDAR_HOUR_ROW_PX;
  return {
    top: `${4 + Math.round(offsetPx)}px`,
    height: `${Math.max(Math.round(heightPx) - 4, 28)}px`,
  };
}

/** Можно перетаскивать только до начала занятия. */
export function canDragSession(session) {
  if (!session?.start_datetime) return false;
  if (session.status === "cancelled" || session.status === "completed") {
    return false;
  }
  return sessionTimeState(session).isUpcoming;
}

/** Перенос занятия на другой час (для drag-and-drop). */
export function shiftSessionToHour(session, dayStr, targetHour) {
  const start = new Date(session.start_datetime);
  const end = new Date(session.end_datetime);
  const durationMs = Math.max(30 * 60000, end.getTime() - start.getTime());
  const newStart = new Date(`${dayStr}T12:00:00`);
  newStart.setHours(targetHour, start.getMinutes(), 0, 0);
  const newEnd = new Date(newStart.getTime() + durationMs);
  return {
    start_datetime: newStart.toISOString(),
    end_datetime: newEnd.toISOString(),
  };
}

/**
 * Проверка переноса: нельзя в прошлое; нельзя если занятие уже началось.
 * @returns {{ ok: true, patch: object } | { ok: false, reason: string }}
 */
export function canRescheduleSessionTo(session, dayStr, targetHour) {
  if (!canDragSession(session)) {
    return {
      ok: false,
      reason: getSessionDragBlockReason(session) || "started",
    };
  }
  const patch = shiftSessionToHour(session, dayStr, targetHour);
  const newStartMs = new Date(patch.start_datetime).getTime();
  const oldStartMs = new Date(session.start_datetime).getTime();
  const now = Date.now();

  if (newStartMs < now) {
    return { ok: false, reason: "past" };
  }
  if (Math.abs(newStartMs - oldStartMs) < 60 * 1000) {
    return { ok: false, reason: "same" };
  }
  return { ok: true, patch };
}

/** Проверка переноса на точное время (форма в карточке занятия). */
export function canRescheduleToTimes(session, startIso) {
  if (!session?.start_datetime || !startIso) {
    return { ok: false, reason: "invalid" };
  }
  if (!canDragSession(session)) {
    return {
      ok: false,
      reason: getSessionDragBlockReason(session) || "started",
    };
  }
  const newStartMs = new Date(startIso).getTime();
  const oldStartMs = new Date(session.start_datetime).getTime();
  const now = Date.now();
  if (newStartMs < now) {
    return { ok: false, reason: "past" };
  }
  if (Math.abs(newStartMs - oldStartMs) < 60 * 1000) {
    return { ok: false, reason: "same" };
  }
  return { ok: true };
}

export function getSessionDragBlockReason(session) {
  if (!session?.start_datetime) return "invalid";
  if (session.status === "cancelled") return "status_cancelled";
  if (session.status === "completed") return "status_completed";
  const st = sessionTimeState(session);
  if (st.isLive) return "started";
  if (st.isPast) return "ended";
  return null;
}

/** Почему нельзя тащить карточку (до начала drag). */
export function sessionDragBlockedMessage(session) {
  return rescheduleBlockedMessage(getSessionDragBlockReason(session));
}

export function rescheduleBlockedMessage(reason) {
  if (reason === "past") {
    return "Нельзя перенести на это время — оно уже прошло";
  }
  if (reason === "started") {
    return "Нельзя перенести — тренировка уже началась";
  }
  if (reason === "ended") {
    return "Нельзя перенести — время занятия уже вышло";
  }
  if (reason === "status" || reason === "status_cancelled") {
    return "Нельзя перенести — занятие отменено";
  }
  if (reason === "status_completed") {
    return "Нельзя перенести — занятие уже завершено";
  }
  if (reason === "same") {
    return "Занятие уже в это время";
  }
  return null;
}

export function sessionTimeState(session) {
  if (!session?.start_datetime || !session?.end_datetime) {
    return { isLive: false, isPast: false, isUpcoming: false };
  }
  const now = Date.now();
  const start = new Date(session.start_datetime).getTime();
  const end = new Date(session.end_datetime).getTime();
  return {
    isLive: now >= start && now <= end,
    isPast: now > end,
    isUpcoming: now < start,
  };
}

/** Сводка переклички с API (present_count — только явные «Был» с marked_at). */
export function sessionRollCallStats(session) {
  const total = Number(session?.enrolled_count ?? session?.expected_count ?? 0);
  const present = Number(session?.present_count ?? 0);
  const marked = Number(session?.marked_count ?? 0);
  return {
    total,
    present,
    marked,
    allMarked: total > 0 && marked >= total,
    allPresent: total > 0 && present >= total,
  };
}

/** Сводка по строкам журнала (надёжнее, если API ещё не обновился). */
export function rollCallStatsFromAttendances(attendances, session) {
  const rows = attendances || [];
  const fromRows = {
    total: rows.length,
    present: rows.filter((a) => attendanceMarkState(a).isPresent).length,
    marked: rows.filter((a) => attendanceMarkState(a).marked).length,
  };
  const api = sessionRollCallStats(session);
  const total = fromRows.total || api.total;
  const present = Math.max(api.present, fromRows.present);
  const marked = Math.max(api.marked, fromRows.marked);
  return {
    total,
    present,
    marked,
    allMarked: total > 0 && marked >= total,
    allPresent: total > 0 && present >= total,
  };
}

export function rollCallHeroLine(roll) {
  if (!roll?.total) return null;
  const { total, present, marked } = roll;
  let tail = "";
  if (present > 0) tail = ` · было ${present}`;
  else if (marked > 0) tail = "";
  else tail = " · нет отметок";
  return `Перекличка ${marked}/${total}${tail}`;
}

export const ABSENCE_REASON_PRESETS = [
  {
    id: "illness",
    label: "Болезнь",
    hint: "Справка или сообщение",
    icon: "🤒",
  },
  {
    id: "unexcused",
    label: "Пропуск",
    hint: "Без предупреждения",
    icon: "✕",
  },
  {
    id: "family",
    label: "Семейные",
    hint: "Уважительная причина",
    icon: "🏠",
  },
  {
    id: "competition",
    label: "Соревнование",
    hint: "Выезд, турнир",
    icon: "🏆",
  },
];

export function absenceReasonLabel(reason) {
  if (!reason) return null;
  const preset = ABSENCE_REASON_PRESETS.find((p) => p.id === reason);
  if (preset) return preset.label;
  const text = String(reason).trim();
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
}

export function attendanceStatusShort(attendance) {
  const state = attendanceMarkState(attendance);
  if (!state.marked) return "Не отмечен";
  if (state.isLate) return "Опоздал";
  if (state.isPresent) return "Был";
  if (state.isExcused) return "Предупредил";
  if (state.isAbsentOnly) {
    const reason = absenceReasonLabel(attendance?.absence_reason);
    return reason ? `Не был · ${reason}` : "Не был";
  }
  return "Отмечен";
}

/** Единый статус занятия для календаря и журнала (не путать «Прошло» и «Завершено»). */
export function sessionDisplayStatus(session) {
  if (session?.status === "cancelled") {
    return { label: "Отменено", tone: "cancelled" };
  }
  const roll = sessionRollCallStats(session);
  const { isLive, isPast } = sessionTimeState(session);
  if (
    session?.status === "completed" ||
    (isPast && roll.total > 0 && roll.allMarked)
  ) {
    return { label: "Завершено", tone: "done" };
  }
  if (isLive) return { label: "Сейчас", tone: "live" };
  if (isPast) return { label: "Прошло", tone: "past" };
  return { label: "Запланировано", tone: "planned" };
}

/** Перекличку можно вести в любое время (кроме отменённых занятий). */
export function canMarkSessionAttendance(session) {
  if (!session?.start_datetime) return false;
  return session.status !== "cancelled";
}

/** Явная отметка (marked_at), а не дефолт absent при создании занятия */
export function attendanceMarkState(attendance) {
  const marked = Boolean(attendance?.marked_at);
  const status = attendance?.status;
  const presentLike = status === "present" || status === "late";
  return {
    marked,
    isPresent: marked && presentLike,
    isLate: marked && status === "late",
    isExcused: marked && status === "excused",
    isAbsentOnly: marked && status === "absent",
    isAbsent:
      marked && (status === "absent" || status === "excused"),
  };
}

/**
 * Повторный тап по уже активной кнопке — снять отметку (clear) и вернуть занятие на абонемент.
 * Без marked_at всегда выставляем новый статус (не toggle).
 */
export function attendanceMarkPayload(attendance, nextStatus) {
  const state = attendanceMarkState(attendance);
  if (!state.marked) {
    return { status: nextStatus };
  }
  if (nextStatus === "present" && state.isPresent && !state.isLate) {
    return { clear: true };
  }
  if (nextStatus === "late" && state.isLate) {
    return { clear: true };
  }
  if (nextStatus === "excused" && state.isExcused) {
    return { clear: true };
  }
  if (nextStatus === "absent" && state.isAbsentOnly) {
    return { clear: true };
  }
  return { status: nextStatus };
}
